import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { AuthLayout } from "./Login";
import { roleLabel } from "../../lib/permissions";

/* Accept invitation — arrived via the Brevo invitation link.
   URL pattern: /accept-invitation/<token>.

   Two-step:
   1. On mount: GET /invitations/by-token/:token to validate + show
      the email + inviter name. This page renders "X invited you" as
      the heading so the user knows what they're signing up for.
   2. Submit: POST /invitations/accept { token, name, password } →
      user is created; we redirect to /login. (We don't auto-login
      because the user just set a password — making them log in
      with it once anchors it in muscle memory.) */
export function AcceptInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get(`/invitations/by-token/${encodeURIComponent(token)}`);
        setInvite(data);
      } catch (err) {
        setLoadError(err instanceof ApiError ? err.message : "This invitation isn't valid.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const valid = pw.length >= 10 && /[A-Za-z]/.test(pw) && /\d/.test(pw);
  const matches = pw === confirm;
  const nameValid = name.trim().length > 0;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!nameValid) { setError("Please enter your full name."); return; }
    if (!valid) { setError("Password must be at least 10 characters and include a letter and a digit."); return; }
    if (!matches) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/invitations/accept", { token, name: name.trim(), password: pw });
      setDone(true);
      setTimeout(() => navigate("/portal-x7k3", { replace: true }), 2500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't complete signup. Try again.");
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <AuthLayout title="Checking invitation…">
        <div className="text-center py-6"><Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto" /></div>
      </AuthLayout>
    );
  }

  if (loadError) {
    return (
      <AuthLayout title="Invitation problem">
        <p className="text-sm text-red-700 bg-red-50 ring-1 ring-red-200 rounded-lg px-3 py-2">{loadError}</p>
        <p className="text-sm text-slate-600 mt-4">
          Ask the person who invited you to send a new link, or <Link to="/portal-x7k3" className="text-blue-600 hover:text-blue-800">sign in</Link> if you already have an account.
        </p>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout title="You're in!">
        <div className="text-center">
          <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-3" />
          <p className="text-sm text-slate-700">Account created. Redirecting you to sign in…</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={`${invite.inviterName} invited you to AbhiGhar`}
      subtitle={`You'll join as ${roleLabel(invite.role)}. We'll use ${invite.email} as your sign-in.`}
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Your full name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
            autoComplete="name"
            className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2.5 focus:outline-none focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Password</label>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            required
            autoComplete="new-password"
            className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2.5 focus:outline-none focus:ring-blue-500"
          />
          <p className={`text-[10px] mt-1 ${valid ? "text-green-600" : "text-slate-400"}`}>
            At least 10 characters, with a letter and a digit
          </p>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Confirm password</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            autoComplete="new-password"
            className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2.5 focus:outline-none focus:ring-blue-500"
          />
          {confirm && !matches && <p className="text-[10px] text-red-600 mt-1">Doesn't match</p>}
        </div>
        {error && (
          <div className="text-sm text-red-700 bg-red-50 ring-1 ring-red-200 rounded-lg px-3 py-2">{error}</div>
        )}
        <button
          type="submit"
          disabled={busy || !nameValid || !valid || !matches}
          className="w-full bg-[#162a52] hover:bg-[#0f1f3d] disabled:opacity-60 text-white font-semibold rounded-lg px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {busy ? "Creating account…" : "Create account"}
        </button>
      </form>
    </AuthLayout>
  );
}
export default AcceptInvitation;
